import { FC, useMemo, useState, useEffect } from 'react';
import styles from './Tabs.module.scss';
import { useHistory, useLocation } from 'react-router-dom';
import { EVENTS_ROOT, ROUTES } from '../../shared/routes';
import { CopyClipboard, Tabs as WixTabs } from 'wix-style-react';
import { useAppSelector } from 'src/hooks';
import { AttendeeQuotaAmount } from 'src/redesign/features/PlanInformation';
import { getSelectedSiteDataSelector } from 'src/redesign/features/SelectedSite/store/selectors';
import { Button } from 'src/redesign/components/Button';
import {
  hasRequestEventCreationRoleSelector,
  isAdminOrOwnerSelector,
  isEventManagerSelector,
  isEventHostManagerSelector,
} from 'src/redesign/features/Account/store/selectors';
import { Loader } from '@components/Loader';
import { GlobalAttendeeSearchModal } from 'src/redesign/components/GlobalAttendeeSearchModal';

const ALL_TABS = [
  {
    id: EVENTS_ROOT,
    title: 'Events',
  },
  {
    id: ROUTES.analytics,
    title: 'Analytics',
  },
  {
    id: ROUTES.design,
    title: 'Design',
  },
  {
    id: ROUTES.integrations,
    title: 'Integrations',
  },
  {
    id: ROUTES.siteSettings,
    title: 'Site Settings',
  },
  {
    id: ROUTES.team,
    title: 'Invite Team',
  },
];

const DESIGN_PATHS = [
  `${ROUTES.design}/brand`,
  `${ROUTES.design}/site`,
  `${ROUTES.design}/event`,
  `${ROUTES.design}/widget`,
  `${ROUTES.design}/ticket`,
  `${ROUTES.design}/communication`,
];

const AVAILABLE_PATH = [`${EVENTS_ROOT}/`, EVENTS_ROOT, ...Object.values(ROUTES), ...DESIGN_PATHS];

export const Tabs: FC = () => {
  const history = useHistory();
  const location = useLocation();
  const currentSite = useAppSelector(getSelectedSiteDataSelector);
  const isAdminOrOwner = useAppSelector(isAdminOrOwnerSelector);
  const isEventManager = useAppSelector(isEventManagerSelector);
  const isEventHostManager = useAppSelector(isEventHostManagerSelector);
  const hasRequestEventCreationRole = useAppSelector(hasRequestEventCreationRoleSelector);
  const [attendeeSearchModalVisible, setAttendeeSearchModalVisible] = useState(false);
  const [showCopied, setShowCopied] = useState(false);

  // Reset "Copied!" text after 3 seconds
  useEffect(() => {
    if (showCopied) {
      const timeout = setTimeout(() => {
        setShowCopied(false);
      }, 3000);
      return () => clearTimeout(timeout);
    }
  }, [showCopied]);

  // Filter tabs based on user role
  const TABS = useMemo(() => {
    if (isAdminOrOwner) {
      return ALL_TABS; // Admin and Owner see all tabs
    } else if (isEventManager) {
      // Event Managers see all tabs except Site Settings and Invite Team
      return ALL_TABS.filter((tab) => tab.id !== ROUTES.siteSettings && tab.id !== ROUTES.team);
    } else if (hasRequestEventCreationRole || isEventHostManager) {
      // Users with REQUEST_EVENT_CREATION or EVENT_HOST_MANAGER role can see Events and Analytics
      return ALL_TABS.filter((tab) => tab.id === EVENTS_ROOT || tab.id === ROUTES.analytics);
    }
    return ALL_TABS; // Default fallback to all tabs
  }, [isAdminOrOwner, isEventManager, hasRequestEventCreationRole, isEventHostManager]);

  const handleTabClick = (link: string) => {
    const hasSiteParam = /:site-id/.exec(link);
    if (hasSiteParam) {
      history.replace(`${link.replace(/:site-id/, currentSite.id)}${location.search}`);
      return;
    }

    history.push({ pathname: link, search: location.search });
  };

  AVAILABLE_PATH.push(`/sites/${currentSite?.id}/team`);

  const activeTab = useMemo(() => {
    if (/team/.exec(location.pathname)) return '/sites/:site-id/team';
    if (/design/.exec(location.pathname)) return ROUTES.design;
    return location.pathname;
  }, [location.pathname]);

  // Check if current path is valid - support design subroutes like /design/communication/:type
  const isValidPath =
    AVAILABLE_PATH.includes(location.pathname) ||
    location.pathname.startsWith(ROUTES.design) ||
    location.pathname.startsWith(EVENTS_ROOT);

  if (!isValidPath) return null;

  if (!currentSite) {
    return <Loader size="small" statusMessage="Loading Site Information" />;
  }
  return (
    <div className={styles.tabsWrapper}>
      <nav className={styles.wrapper}>
        <div className={styles.siteInformation}>
          <span className={styles.siteName}>{currentSite?.site_display_name}</span>
          <div className={styles.controls}>
            {!isEventHostManager && <AttendeeQuotaAmount />}
            {!isEventHostManager && (
              <CopyClipboard
                value={
                  currentSite?.site_page_url ||
                  `https://ticketspotapp.com/${currentSite?.subdomain}`
                }
              >
                {({ copyToClipboard }) => {
                  return (
                    <Button
                      priority="secondary"
                      size="large"
                      onClick={() => {
                        copyToClipboard && copyToClipboard();
                        setShowCopied(true);
                      }}
                      prefixIcon={<i className="pi pi-copy" style={{ fontSize: '18px' }}></i>}
                    >
                      {showCopied ? 'Copied!' : 'Event Site URL'}
                    </Button>
                  );
                }}
              </CopyClipboard>
            )}
            {!isEventHostManager && (
              <Button
                priority="secondary"
                size="large"
                onClick={() => setAttendeeSearchModalVisible(true)}
                prefixIcon={<i className="pi pi-search" style={{ fontSize: '18px' }}></i>}
              >
                Search
              </Button>
            )}
          </div>
        </div>
        <WixTabs
          className={styles.tabs}
          items={TABS}
          onClick={(item) => handleTabClick(item.id as string)}
          activeId={activeTab}
          hasDivider={false}
        />
      </nav>
      <GlobalAttendeeSearchModal
        visible={attendeeSearchModalVisible}
        onHide={() => setAttendeeSearchModalVisible(false)}
      />
    </div>
  );
};
